import { MapLayerOpacityLevel } from 'map/common-map-models';
import { getKeysWithDifferentValues } from 'utils/arrayHelpers';
import { Geoname } from 'utils/constants';

import {
  DiseaseActivityParams,
  DiseaseActivityParamsPayload,
  PanelType,
} from './diseaseActivityPanels.slice';

export const userLocationIdParam2AoiPayload = (id: number) => (id === 0 ? undefined : id);

export enum ActiveMapLayer {
  None,
  LLP,
  DLP,
  DELP,
  EDP,
}
export type DiseaseActivityPanelState = {
  activePanel: PanelType;
  isVisibleDLP: boolean;
  isVisibleDELP: boolean;
  isVisibleEDP: boolean;
  isVisibleTRANSPAR: boolean;
  isVisibleHistory: boolean;
};

export type DiseaseActivityPanelReloadFlags = {
  needToReloadLocations: boolean;
  needToReloadDiseases: boolean;
  needToReloadDiseaseActivityList: boolean;
  needToReloadDiseaseActivityDetails: boolean;
  needToReloadDiseaseActivityUpdates: boolean;
  needtoReloadRiskParameters: boolean;
  justClosedDELP: boolean;
  justClosedEDP: boolean;
};

export type MapLayerVisibility = 'visible' | 'hidden';
export type MapLayerListMode = 'all' | 'selected' | 'hidden';
export type MapLayersState = {
  activeLayer: ActiveMapLayer;
  aoiLayerMode: MapLayerListMode;
  aoiLayerFade: MapLayerOpacityLevel;
  eventsLayerVisibility: MapLayerVisibility;
  outbreakLayerMode: MapLayerListMode;
  outbreakLayerFade: MapLayerOpacityLevel;
  airpoirtLayerVisibility: MapLayerVisibility;
};

export function mapDiseaseActivityParamsToPanelState(
  p: DiseaseActivityParamsPayload
): DiseaseActivityPanelState {
  const { userLocationId, diseaseId, countryId, hasActivity, hasParameters } = p;

  return {
    activePanel:
      userLocationId != null && diseaseId && hasActivity
        ? PanelType.DiseaseActivityUpdatesPanel
        : userLocationId != null && diseaseId && countryId && hasParameters
        ? PanelType.ParametersPanel
        : userLocationId != null && diseaseId && countryId
        ? PanelType.DiseaseActivityDetailsPanel
        : userLocationId != null && diseaseId && hasParameters // TODO: RisksProjectionCard.onlick is not set in DELP, do we need to check for this route?
        ? PanelType.ParametersPanel
        : userLocationId != null && diseaseId
        ? PanelType.DiseaseActivityListPanel
        : userLocationId != null
        ? PanelType.DiseasesPanel
        : PanelType.MyLocationsPanel,
    isVisibleDLP: !!(userLocationId != null),
    isVisibleDELP: !!(userLocationId != null && diseaseId),
    isVisibleEDP: !!(userLocationId != null && diseaseId && countryId),
    isVisibleTRANSPAR: !!(userLocationId != null && diseaseId && countryId && hasParameters),
    isVisibleHistory: !!(userLocationId != null && diseaseId && hasActivity),
  };
}

export function mapWhatPanelsNeedToReload(
  pOld: DiseaseActivityParams,
  pNew: DiseaseActivityParams
): DiseaseActivityPanelReloadFlags {
  const { isVisibleDLP, isVisibleDELP, isVisibleEDP, isVisibleTRANSPAR, isVisibleHistory } =
    mapDiseaseActivityParamsToPanelState(pNew);
  const {
    isActive: isNewlyActive,
    userLocationId: userLocationIdChanged,
    diseaseId: diseaseIdChanged,
    speciesId: speciesIdChanged,
    countryId: countryIdChanged,
    activityDate: activityDateChanged,
    hasActivity: hasActivityChanged,
    hasParameters: hasParametersChanged,
  } = getKeysWithDifferentValues(pOld, {
    ...pNew,
    isActive: true,
  });

  const isChangedDELP = diseaseIdChanged || speciesIdChanged;
  const isChangedEDP = countryIdChanged;

  return {
    needToReloadLocations: isNewlyActive,
    needToReloadDiseases: isVisibleDLP && userLocationIdChanged,
    needToReloadDiseaseActivityList:
      isVisibleDELP && (userLocationIdChanged || diseaseIdChanged || speciesIdChanged),
    needToReloadDiseaseActivityDetails:
      isVisibleEDP &&
      (userLocationIdChanged || diseaseIdChanged || speciesIdChanged || countryIdChanged),
    needToReloadDiseaseActivityUpdates:
      isVisibleHistory &&
      (userLocationIdChanged ||
        diseaseIdChanged ||
        speciesIdChanged ||
        countryIdChanged ||
        hasActivityChanged ||
        activityDateChanged),
    needtoReloadRiskParameters:
      isVisibleTRANSPAR &&
      (userLocationIdChanged ||
        diseaseIdChanged ||
        speciesIdChanged ||
        countryIdChanged ||
        hasParametersChanged),
    justClosedDELP: !isVisibleDELP && isChangedDELP,
    justClosedEDP: !isVisibleEDP && isChangedEDP,
  };
}

export function mapDiseaseActivityParamsToMapState(
  p: DiseaseActivityParamsPayload,
  isProximalLayerEnabled: boolean
): MapLayersState {
  const { userLocationId, diseaseId, countryId } = p;

  const isGlobalSelected = userLocationId === Geoname.GLOBAL_VIEW;
  const activeLayer =
    userLocationId != null && diseaseId && countryId
      ? ActiveMapLayer.EDP
      : userLocationId != null && diseaseId
      ? ActiveMapLayer.DELP
      : userLocationId != null
      ? ActiveMapLayer.DLP
      : ActiveMapLayer.LLP;

  return {
    activeLayer: activeLayer,
    aoiLayerMode:
      activeLayer === ActiveMapLayer.LLP
        ? 'all'
        : userLocationId && !isGlobalSelected
        ? 'selected'
        : 'hidden',
    aoiLayerFade: isProximalLayerEnabled
      ? 'proxpar'
      : activeLayer === ActiveMapLayer.EDP
      ? 'details'
      : 'none',
    eventsLayerVisibility: activeLayer === ActiveMapLayer.DLP ? 'visible' : 'hidden',
    outbreakLayerMode:
      activeLayer === ActiveMapLayer.DELP
        ? 'all'
        : activeLayer === ActiveMapLayer.EDP
        ? 'selected'
        : 'hidden',
    outbreakLayerFade: isProximalLayerEnabled
      ? 'proxpar'
      : activeLayer === ActiveMapLayer.EDP
      ? 'details'
      : 'none',
    airpoirtLayerVisibility: activeLayer === ActiveMapLayer.EDP ? 'visible' : 'hidden',
  };
}
